// 장비함·대장간 강화·줍기 띠의 표시 모델(D2a·D2b, 순수 로직). DOM은 gearPanel.ts·lootFeed.ts가 그린다.
// 수치는 게임 규칙(shared/gear.ts)에서 바로 만든다(화면 확률·천장·비용이 실제 판정과 어긋나지 않게).
import { GRADES, ENHANCE_PITY, NAMED_COLOR } from '../shared/content/items';
import { gearDefIconCandidates, gradeBadgePath } from './itemIcons';
import { STONE_TIERS, planStoneConversion } from '../shared/content/economy';
import { GEM_COLORS, gemEffectText, gemName, isGemCode } from '../shared/content/consumables';
import { SOCKET_CAP } from '../shared/content/items';
import { ENHANCE_MAX } from '../shared/content/world';
import type { AffixId, EquipSlot, Grade, HeroId } from '../shared/content/types';
import {
  activeSets, affixDef, enhanceChance, uniqueOf, namedOf, tradeInfo, equipDef, isDownStep, itemBaseStats, itemLookTier, itemName, nextEnhanceCost, pityGauge, salvageValue, socketsOf, type GearAffix, type GearItem,
} from '../shared/gear';
import { isLegacyItemId } from '../shared/content/legacyGear';
import { SLOT_ORDER, protectPrice, type EnhanceLog, type GearCode } from '../shared/gearLedger';
import type { GearNoticeView, GearResultView, GearView } from '../shared/gearView';
import type { Tone, TxMessage } from './rpgModel';

export const SLOT_KO: Readonly<Record<EquipSlot, string>> = { weapon: '무기', helm: '투구', armor: '갑옷', gloves: '장갑', boots: '신발', amulet: '목걸이' };
const GRADE_RANK: Readonly<Record<Grade, number>> = { common: 0, rare: 1, epic: 2, set: 3, legend: 4 };
export function gradeName(g: Grade): string { return GRADES.find((x) => x.id === g)?.name ?? g; }
export function gradeColor(g: Grade): string { return GRADES.find((x) => x.id === g)?.color ?? '#e8e8e8'; }
const pct = (v: number, d = 1): string => (Math.round(v * 100 * 10 ** d) / 10 ** d).toFixed(d).replace(/\.0+$/, '') + '%';
const num = (v: number): string => v.toLocaleString('ko-KR');

/** 옵션 한 줄(예: "공격력 % +5.2%") */
export function affixText(a: GearAffix): string {
  const d = affixDef(a.id as AffixId);
  // 드롭 풀에서 뺀(효과 없는) 옵션·모르는 옵션은 효과가 없다고 밝힌다(이전에 굴린 개체가 있을 수 있다)
  const live = !!d && d.slots.length > 0;
  return (d ? d.name : a.id) + ' +' + pct(a.v) + (live ? '' : ' (효과 없음)');
}

export interface GearCard {
  readonly uid: string;
  readonly name: string;
  readonly grade: Grade;
  readonly gradeName: string;
  readonly color: string;
  readonly slot: EquipSlot | null;
  readonly slotName: string;
  readonly ilvl: number;
  readonly enh: number;
  /** 기본 능력치 줄(등급·강화 배율 반영) */
  readonly stats: readonly string[];
  readonly affixes: readonly string[];
  readonly equipped: boolean;
  /** 다른 영웅 전용 무기 */
  readonly otherHero: boolean;
  /** 정리하면 받는 것(문구) */
  readonly salvage: string;
  /** 전설 고유 효과 문구(없으면 '') */
  readonly unique: string;
  /** 이름 있는 유니크 개체 여부(툴팁: 주황 이름 + '유니크' 표시) */
  readonly named: boolean;
  /** 이름 있는 유니크 효과 줄(def.desc), 아니면 빈 배열 */
  readonly namedLines: readonly string[];
  /** 거래 문구 '거래 가능 · 시세 기준 N골드'(유니크만, 아니면 '') */
  readonly trade: string;
  /** 유니크 lore 한 줄(없으면 '') */
  readonly lore: string;
  /** 소켓 줄(D2c, 소켓 없는 등급은 '') 예: "소켓 맑은 홍옥 · 빈 칸" */
  readonly sockets: string;
  /** 아이콘 그림 경로(public/assets 기준, assetUrl 전) — gearIconPath */
  readonly icon: string;
  /** 전용 그림을 못 읽을 때 차례로 쓰는 대체 그림(등급 없는 드롭 그림 → 단계 그림 gearIconFallback). 비면 대체 없음 */
  readonly iconFallbacks: readonly string[];
  /** 등급 배지 그림(세트·전설만, 아니면 null) — 아이콘 오른쪽 아래 모서리 */
  readonly badge: string | null;
}

/**
 * 장비 단계 그림(ECONOMY 14절 장비 통합): 부위 × 외형 단계(itemLookTier) 그림 한 벌. 옛 상점 장비는 원래 그림을 그대로 쓴다.
 * 무기는 영웅 무기 종류(카엘 대검·라이라 장궁)별, 나머지 부위는 공용. 그림 파일은 public/assets/ui/items/.
 * 2026-10-27부터는 드롭·세트 전용 그림(gearIconPath)을 못 읽을 때의 대체 그림이다
 */
export function gearIconFallback(item: GearItem): string {
  const d = equipDef(item.def);
  if (!d) return 'ui/items/sword_worn.webp';
  const legacy = d.id.startsWith('eq_legacy_') ? d.id.slice('eq_legacy_'.length) : '';
  if (legacy && isLegacyItemId(legacy)) return 'ui/items/' + legacy + '.webp';
  const t = itemLookTier(item);
  switch (d.slot) {
    case 'weapon':
      if (d.heroLock === 'lyra') return 'ui/items/bow_t' + t + '.webp';
      return 'ui/items/' + (t === 3 ? 'sword_ember' : t === 2 ? 'sword_steel' : 'sword_training') + '.webp';
    case 'armor': return 'ui/items/' + (t === 3 ? 'armor_crimson' : t === 2 ? 'armor_chain' : 'armor_travel') + '.webp';
    default: return 'ui/items/' + d.slot + '_t' + t + '.webp';
  }
}

/**
 * 장비 아이콘 후보(앞에서부터 시도): 정의 전용 그림(마을 상점 = 그 마을 테마, 지역 드롭 = drop_r<r>_…[_c|_r] 개체 등급별,
 * 세트 = set_<세트>_<부위>, 2026-10-27 회차 ui/itemIcons) → 마지막은 늘 단계 그림(gearIconFallback).
 * 드롭 일반·희귀 그림이 아직 없으면 접미사 없는(영웅 등급) 그림, 그것도 없으면 단계 그림
 */
export function gearIconCandidates(item: GearItem): string[] {
  const fb = gearIconFallback(item);
  const d = equipDef(item.def);
  const list = d ? gearDefIconCandidates(d.id, item.grade) : [];
  return list.includes(fb) ? list : [...list, fb];
}
/** 장비 아이콘 첫 후보 */
export function gearIconPath(item: GearItem): string { return gearIconCandidates(item)[0]; }

/** 마우스 툴팁 글(tooltip.ts): 이름(+강화) / 등급·부위·레벨 / 능력치·옵션 한 줄 */
export function gearTipText(c: Pick<GearCard, 'name' | 'enh' | 'gradeName' | 'slotName' | 'ilvl' | 'stats' | 'affixes'>): string {
  return [c.name + (c.enh > 0 ? ' +' + c.enh : ''), c.gradeName + ' ' + c.slotName + ' · Lv ' + c.ilvl, [...c.stats, ...c.affixes].join(' · ')].filter((x) => x).join('\n');
}

/** 비교 한 줄(아이템 단위): 지금 장착한 같은 부위 장비 → 고른 장비 */
export interface CompareRow { readonly label: string; readonly from: string; readonly to: string; readonly up: boolean; readonly key: string }
const BASE_LABEL: Readonly<Record<'attack' | 'maxHp' | 'def' | 'crit', string>> = { attack: '공격력', maxHp: '최대 HP', def: '방어', crit: '치명 확률' };
/**
 * 고른 장비와 같은 부위에 장착한 장비의 능력치 차이(기본 능력치 + 옵션 합). 같은 값은 뺀다.
 * cur가 없으면(빈 칸) 0과 비교한다. 퍼센트 값(치명·옵션)은 %로, 고정 값은 정수로 보인다
 */
export function gearCompare(item: GearItem, cur: GearItem | null): CompareRow[] {
  const out: CompareRow[] = [];
  const a = cur ? itemBaseStats(cur) : { attack: 0, maxHp: 0, def: 0, crit: 0 }, b = itemBaseStats(item);
  for (const k of ['attack', 'maxHp', 'def', 'crit'] as const) {
    if (a[k] === b[k]) continue;
    const f = k === 'crit' ? (v: number) => pct(v) : (v: number) => num(v);
    out.push({ key: k, label: BASE_LABEL[k], from: f(a[k]), to: f(b[k]), up: b[k] > a[k] });
  }
  const sum = (it: GearItem | null): Map<string, number> => {
    const m = new Map<string, number>();
    if (it) for (const x of it.affixes) m.set(x.id, (m.get(x.id) ?? 0) + x.v);
    return m;
  };
  const am = sum(cur), bm = sum(item);
  for (const id of [...new Set([...am.keys(), ...bm.keys()])]) {
    const x = am.get(id) ?? 0, y = bm.get(id) ?? 0;
    if (Math.abs(x - y) < 1e-9) continue;
    const d = affixDef(id as AffixId);
    // 기본 능력치와 이름이 같은 옵션(방어 +3 / 방어 +3%)은 % 를 붙여 두 줄을 구분한다
    let label = d ? d.name : id;
    if (out.some((r) => r.label === label)) label += ' %';
    out.push({ key: 'af:' + id, label, from: pct(x), to: pct(y), up: y > x });
  }
  return out;
}

export function gearCard(item: GearItem, equipped: boolean, hero = 'kael'): GearCard {
  const d = equipDef(item.def);
  const b = itemBaseStats(item);
  const stats: string[] = [];
  if (b.attack) stats.push('공격력 +' + num(b.attack));
  if (b.maxHp) stats.push('최대 HP +' + num(b.maxHp));
  if (b.def) stats.push('방어 +' + num(b.def));
  if (b.crit) stats.push('치명 확률 +' + pct(b.crit));
  const sv = salvageValue(item);
  const nu = namedOf(item);
  const ti = nu ? tradeInfo(item) : null;
  return {
    uid: item.uid, name: itemName(item), grade: item.grade, gradeName: gradeName(item.grade), color: nu ? NAMED_COLOR : gradeColor(item.grade),
    named: !!nu, namedLines: nu ? nu.desc : [],
    trade: ti && ti.tradable ? '거래 가능 · 시세 기준 ' + num(ti.marketBase) + '골드' : '',
    lore: nu ? nu.lore : '',
    slot: d?.slot ?? null, slotName: d ? SLOT_KO[d.slot] : '?', ilvl: item.ilvl, enh: item.enh, stats, affixes: item.affixes.map(affixText), equipped,
    otherHero: !!d?.heroLock && d.heroLock !== hero,
    unique: (() => { const u = uniqueOf(item); return u ? '고유 · ' + u.name + ': ' + u.desc : ''; })(),
    sockets: SOCKET_CAP[item.grade] > 0 ? '소켓 ' + socketsOf(item).map((c) => (c >= 0 ? gemName(c) + '(' + gemEffectText(c) + ')' : '빈 칸')).join(' · ') : '',
    salvage: sv.stones > 0 ? STONE_TIERS[sv.tier].name + ' ×' + sv.stones : sv.gold > 0 ? num(sv.gold) + ' 골드' : '없음',
    ...(() => { const c = gearIconCandidates(item); return { icon: c[0], iconFallbacks: c.slice(1) }; })(), badge: gradeBadgePath(item.grade),
  };
}

/** 장착 6칸(빈 칸은 null) */
export function equippedCards(view: Pick<GearView, 'items' | 'equipped'>, hero = 'kael'): (GearCard | null)[] {
  return SLOT_ORDER.map((k) => {
    const u = view.equipped[k];
    const it = u ? view.items.find((x) => x.uid === u) : undefined;
    return it ? gearCard(it, true, hero) : null;
  });
}

/** 장착 세트 효과 줄(장비함 표시): 세트 이름·부위 수와 2·4세트 효과(켜짐 여부). 적용 판정은 sumGear와 같은 activeSets */
export function setRows(view: Pick<GearView, 'items' | 'equipped'>): { name: string; count: number; lines: { text: string; on: boolean }[] }[] {
  const eq = SLOT_ORDER.map((k) => { const u = view.equipped[k]; return u ? view.items.find((x) => x.uid === u) ?? null : null; });
  return activeSets(eq).map((s) => ({
    name: s.def.name, count: s.count,
    lines: [{ text: '2세트: ' + s.def.bonus2, on: s.on2 }, { text: '4세트: ' + s.def.bonus4, on: s.on4 }],
  }));
}

/** 다른 영웅 무기 칸에 낀 장비(uid → 영웅). 지금 영웅 칸(equipped)에 있는 것은 뺀다. heroWeapons가 없는 옛 뷰는 빈 맵 */
export function otherHeroWeapons(view: Pick<GearView, 'equipped' | 'heroWeapons'>): Map<string, HeroId> {
  const mine = new Set(Object.values(view.equipped).filter((u): u is string => !!u));
  const out = new Map<string, HeroId>();
  for (const [h, u] of Object.entries(view.heroWeapons ?? {})) if (u && !mine.has(u)) out.set(u, h as HeroId);
  return out;
}
const HERO_KO: Readonly<Record<string, string>> = { kael: '카엘', lyra: '라이라', seren: '세렌', brann: '브란' };
/** 가방에서 빠진 다른 영웅 착용 무기 안내 줄("라이라 착용 중: 이름"). 없으면 [] */
export function otherHeroWornRows(view: Pick<GearView, 'items' | 'equipped' | 'heroWeapons'>): { uid: string; text: string }[] {
  const out: { uid: string; text: string }[] = [];
  for (const [u, h] of otherHeroWeapons(view)) {
    const it = view.items.find((x) => x.uid === u);
    if (it) out.push({ uid: u, text: (HERO_KO[h] ?? h) + ' 착용 중: ' + itemName(it) });
  }
  return out;
}

/** 가방(장착 제외, 다른 영웅이 낀 무기도 제외): 등급 높은 순 → 아이템 레벨 → 강화 단계 */
/** hero = 지금 영웅(다른 영웅 전용 무기 표시·장착 단추 판정). 넘기지 않으면 카엘 — 라이라 가방에서 활 장착이 막히던 결함(lyra-weapon-slot QA) */
export function bagCards(view: Pick<GearView, 'items' | 'equipped' | 'heroWeapons'>, hero = 'kael'): GearCard[] {
  const eq = new Set(SLOT_ORDER.map((k) => view.equipped[k]).filter((u): u is string => !!u));
  for (const u of otherHeroWeapons(view).keys()) eq.add(u);
  return view.items
    .filter((it) => !eq.has(it.uid))
    .slice()
    .sort((a, b) => GRADE_RANK[b.grade] - GRADE_RANK[a.grade] || b.ilvl - a.ilvl || b.enh - a.enh || (a.uid < b.uid ? -1 : 1))
    .map((it) => gearCard(it, false, hero));
}

export interface EnhanceModel {
  readonly uid: string;
  readonly name: string;
  readonly enh: number;
  readonly to: number;
  readonly max: boolean;
  /** 지금 상한에 막힘 */
  readonly capped: boolean;
  readonly capText: string;
  /** 실효 확률·기본 확률·천장 배율(화면 공개, PRD 4.3) */
  readonly chance: string;
  readonly base: string;
  readonly pityMult: string;
  /** 천장 게이지 0..1(이 단계 배율이 3배까지 간 비율) */
  readonly gauge: number;
  readonly fails: number;
  /** 실패하면 하락할 수 있는 단계(+13~+15) */
  readonly downRisk: boolean;
  readonly failText: string;
  readonly stoneName: string;
  readonly stonesNeed: number;
  readonly stonesHave: number;
  readonly goldNeed: number;
  readonly goldHave: number;
  readonly affordable: boolean;
  /** 강화석이 모자랄 때 아래 등급 5:1 변환 안내(필요 없으면 ''). 변환 수수료는 goldNeed에 이미 들어 있다 */
  readonly convText: string;
  /** 보호 주문서 칸: 해금(지역6 클리어)·보유 수 */
  readonly protectUnlocked: boolean;
  readonly protects: number;
  /** 강화 전후 주 능력치(공격 또는 HP) */
  readonly statFrom: string;
  readonly statTo: string;
}

export function enhanceModel(view: Pick<GearView, 'items' | 'stones' | 'protects' | 'enhanceCap' | 'protectUnlocked'>, uid: string, gold: number): EnhanceModel | null {
  const it = view.items.find((x) => x.uid === uid);
  if (!it || !equipDef(it.def)) return null;
  const max = it.enh >= ENHANCE_MAX;
  const c = enhanceChance(it);
  const cost = nextEnhanceCost(it);
  const conv = planStoneConversion(view.stones, cost.tier, Math.max(0, cost.stones - (view.stones[cost.tier] ?? 0)));
  const down = isDownStep(it);
  const b = itemBaseStats(it);
  // 부위의 주 능력치: 무기·장갑 공격, 방어구 HP, 목걸이 치명(강화 배율을 받는 실제 값)
  const key: 'attack' | 'maxHp' | 'def' | 'crit' = b.attack ? 'attack' : b.maxHp ? 'maxHp' : b.crit ? 'crit' : 'def';
  const label = key === 'attack' ? '공격력' : key === 'maxHp' ? '최대 HP' : key === 'crit' ? '치명 확률' : '방어';
  const base = b[key];
  const nextItem = { ...it, enh: max ? it.enh : c.to };
  const next = itemBaseStats(nextItem)[key];
  const fmt = (v: number): string => (key === 'crit' ? pct(v, 2) : num(v));
  const capped = !max && c.to > view.enhanceCap;
  return {
    uid, name: itemName(it), enh: it.enh, to: c.to, max, capped,
    capText: capped ? `지금 강화 상한 +${view.enhanceCap} (${view.enhanceCap < 12 ? '다음 마을 도착' : '지역6 클리어'} 시 상승)` : '',
    chance: pct(c.chance), base: pct(c.base), pityMult: '×' + (Math.round(c.mult * 100) / 100).toFixed(2), gauge: pityGauge(it), fails: c.fails,
    downRisk: down, failText: down ? '실패하면 50% 확률로 1단계 하락(보호 주문서로 막음)' : '실패해도 단계 유지',
    stoneName: STONE_TIERS[cost.tier].name, stonesNeed: cost.stones, stonesHave: view.stones[cost.tier] ?? 0, goldNeed: cost.gold + conv.fee, goldHave: gold,
    affordable: conv.ok && gold >= cost.gold + conv.fee,
    convText: conv.made <= 0 ? '' : conv.ok
      ? '아래 등급 변환 ' + conv.take.map((n, k) => (n > 0 ? STONE_TIERS[k].name + ' ' + n : '')).filter(Boolean).join(' · ') + ` → ${STONE_TIERS[cost.tier].name} ${conv.made} (수수료 ${num(conv.fee)} G 포함)`
      : '강화석이 모자랍니다(아래 등급 5개 = 위 등급 1개로 변환해도 부족)',
    protectUnlocked: view.protectUnlocked, protects: view.protects,
    statFrom: label + ' ' + fmt(base), statTo: label + ' ' + fmt(next),
  };
}

/** 장비 저장 상태 안내(정상이면 null). corrupt는 두 단계 확인 뒤 새로 시작할 수 있다 */
export function gearModeNotice(mode: GearView['mode']): { text: string; recover: boolean } | null {
  if (mode === 'corrupt') return { text: '장비 기록이 손상되어 읽을 수 없습니다. 새로 시작하면 손상된 원본은 백업에 남기고 빈 장비함으로 이어 갑니다.', recover: true };
  if (mode === 'future') return { text: '더 새로운 버전의 게임이 저장한 장비 기록입니다. 최신 버전에서 열어 주세요(기록은 그대로 둡니다).', recover: false };
  if (mode === 'unavailable') return { text: '저장소를 쓸 수 없어 장비 변경이 저장되지 않습니다.', recover: false };
  return null;
}

/** 강화 요청에 보호 주문서를 쓸지: 고른 상태 + 하락 구간 + 해금 + 1장 이상. 하나라도 아니면 선택을 푼다 */
export function protectRequested(want: boolean, m: Pick<EnhanceModel, 'downRisk' | 'protectUnlocked' | 'protects'>): boolean {
  return want && m.downRisk && m.protectUnlocked && m.protects > 0;
}

/** 천장 설명(화면 공개 문구) */
export const PITY_RULE = `실패할 때마다 이 단계 성공 확률 +${Math.round(ENHANCE_PITY.step * 100)}% (최대 ${ENHANCE_PITY.max}배). 성공하면 초기화, 하락해도 유지.`;

export function outcomeText(outcome: string, from: number, to: number): TxMessage {
  if (outcome === 'success') return { text: `강화 성공! +${from} → +${to}`, tone: 'ok' };
  if (outcome === 'protected') return { text: `강화 실패 — 보호 주문서가 하락을 막았습니다(+${to} 유지)`, tone: 'warn' };
  if (outcome === 'down') return { text: `강화 실패 — +${from} → +${to} 하락`, tone: 'err' };
  return { text: `강화 실패 — +${to} 유지, 천장 게이지가 올랐습니다`, tone: 'warn' };
}

const CODE_TEXT: Readonly<Record<GearCode, [string, Tone]>> = {
  ok: ['완료', 'ok'],
  duplicate: ['이미 처리한 요청입니다.', 'warn'],
  auto_salvaged: ['가방이 가득 차 바로 정리했습니다.', 'warn'],
  bag_full: ['가방이 가득 차 벗을 수 없습니다. 가방 장비를 정리하세요.', 'warn'],
  unknown_item: ['장비를 찾을 수 없습니다.', 'err'],
  equipped: ['장착 중인 장비는 정리할 수 없습니다. 먼저 벗으세요.', 'warn'],
  wrong_hero: ['다른 영웅 전용 무기입니다.', 'warn'],
  max: ['이미 최대 강화(+15)입니다.', 'warn'],
  capped: ['지금은 이 단계까지만 강화할 수 있습니다.', 'warn'],
  no_stones: ['강화석이 부족합니다.', 'err'],
  no_gold: ['골드가 부족합니다.', 'err'],
  no_protect: ['보호 주문서가 없습니다.', 'err'],
  protect_locked: ['보호 주문서는 지역6 클리어 뒤에 열립니다.', 'warn'],
  limit: ['이번 주 구매 한도를 채웠습니다.', 'warn'],
  readonly: ['지금은 장비를 바꿀 수 없습니다(저장 잠금 또는 다른 창에서 거래 중).', 'err'],
  save_failed: ['저장하지 못해 취소했습니다. 다시 시도해 주세요.', 'err'],
  locked: ['아직 열리지 않았습니다(다음 마을에서 열립니다).', 'warn'],
  no_item: ['가진 것이 없습니다.', 'err'],
  bad_socket: ['그 소켓에는 할 수 없습니다.', 'warn'],
  need15: ['초월은 +15 장비를 낀 부위만 할 수 있습니다.', 'warn'],
  denied: ['대장간이나 가방 창에서만 할 수 있습니다.', 'warn'],
  // C4 재련(장부 GearCode 추가에 맞춘 문구 두 줄 — 재련 화면은 L3)
  pending: ['먼저 재련 결과를 고르세요.', 'warn'],
  stale: ['다른 창에서 이미 처리했습니다. 다시 열어 주세요.', 'warn'],
};

/** 마지막 장비 명령 결과 문구 */
export function gearMessage(last: GearResultView): TxMessage {
  if (last.kind === 'enhance' && last.code === 'ok') return outcomeText(last.outcome, last.from, last.to);
  if (last.code === 'ok') {
    const t = last.kind === 'recover' ? '장비 기록을 새로 시작했습니다. 손상된 원본은 백업에 남겨 두었습니다.' : last.kind === 'equip' ? '장착했습니다.' : last.kind === 'unequip' ? '벗었습니다.' : last.kind === 'salvage' ? '정리했습니다.' : last.kind === 'buyProtect' ? '보호 주문서를 샀습니다.'
      : last.kind === 'buy' ? '샀습니다.' : last.kind === 'craft' ? '비약을 만들었습니다.' : last.kind === 'elixir' ? '비약을 마셨습니다.' : last.kind === 'socket' ? '보석을 박았습니다.' : last.kind === 'unsocket' ? '보석을 뺐습니다.' : last.kind === 'combine' ? '보석을 합성했습니다.' : last.kind === 'buyGear' ? '샀습니다. 가방(장비함)에서 장착하세요.' : '완료';
    return { text: t, tone: 'ok' };
  }
  const [text, tone] = CODE_TEXT[last.code];
  return { text, tone };
}

const NOTICE_KIND: Readonly<Record<string, string>> = {
  equip: '장착', unequip: '벗기', enhance: '강화', salvage: '정리', buyProtect: '보호 주문서 구매', pickup: '주운 장비', recover: '장비 기록 복구',
  buy: '구매', buyGear: '장비 구매', craft: '비약 제작', use: '물약·주문서 사용', elixir: '비약', socket: '보석 박기', unsocket: '보석 빼기', combine: '보석 합성',
};
/**
 * 성공이라 알렸던 연산이 나중에 취소된 알림(c015, Ev.GearNotice). 다른 창(탭)에서 장비가 바뀌어 다시 적용할 수 없었다는 것을 한 줄로 알린다.
 * 예: "강화 취소: 강화석이 부족합니다. (다른 창에서 장비가 바뀜)"
 */
export function gearNoticeText(n: Pick<GearNoticeView, 'kind' | 'code'>, count = 1): TxMessage {
  const what = NOTICE_KIND[n.kind] ?? '장비 변경';
  const why = CODE_TEXT[n.code]?.[0] ?? '';
  const more = count > 1 ? ` 외 ${count - 1}건` : '';
  return { text: `${what}${more} 취소: ${why} (다른 창에서 장비가 바뀜)`, tone: 'err' };
}

/** 강화 장부 최근 n줄(새 것부터) */
export function logRows(log: readonly EnhanceLog[], items: readonly GearItem[], n = 8): { text: string; tone: Tone }[] {
  const out: { text: string; tone: Tone }[] = [];
  for (let i = log.length - 1; i >= 0 && out.length < n; i--) {
    const l = log[i];
    const it = items.find((x) => x.uid === l.uid);
    const nm = it ? (equipDef(it.def)?.name ?? '장비') : '정리한 장비';
    const r = l.outcome === 'success' ? '성공' : l.outcome === 'protected' ? '보호' : l.outcome === 'down' ? '하락' : '실패';
    out.push({ text: `${nm} +${l.from}→+${l.to} ${r} (${pct(l.chance)}${l.protect ? '·주문서' : ''})`, tone: l.outcome === 'success' ? 'ok' : l.outcome === 'down' ? 'err' : 'warn' });
  }
  return out;
}

/** 보호 주문서 상점 줄 */
export function protectShopText(town: number, unlocked: boolean): string {
  return unlocked ? `보호 주문서 ${num(protectPrice(town))} 골드 (주 2장)` : '보호 주문서: 지역6 클리어 뒤 대장간에서 판매';
}

// ── 줍기 띠(화면 가장자리) ─────────────────────────
const GRADE_BY_INDEX: readonly Grade[] = ['common', 'rare', 'epic', 'set', 'legend'];
/** Ev.LootPickup 한 건 → 띠 문구(문구 정본·시험용. 지금 줍기 띠는 ui/dropInfo.ts의 이름·그림·종류 태그를 쓴다 — 이 함수는 지우지 않고 시험이 잠가 둔다)(장비: "희귀 장갑", 강화석: "강화석 ×2"). by 1 = 펫, flags & 1 = 자동 정리 */
export function lootFeedText(kind: number, gradeIndex: number, detail: number, by: number, salvaged: boolean): { text: string; color: string } {
  if (kind === 1) return { text: '강화석 ×' + detail + (by === 1 ? ' · 펫' : ''), color: '#9fc3e6' };
  // 보석(D2c): detail = 보석 코드, gradeIndex = 색 번호
  if (kind === 2) return { text: (isGemCode(detail) ? gemName(detail) : '보석') + (by === 1 ? ' · 펫' : ''), color: GEM_COLORS[Math.max(0, Math.min(GEM_COLORS.length - 1, gradeIndex))].color };
  const g = GRADE_BY_INDEX[Math.max(0, Math.min(4, gradeIndex))];
  const slot = detail >= 0 && detail < SLOT_ORDER.length ? SLOT_KO[SLOT_ORDER[detail]] : '장비';
  return { text: gradeName(g) + ' ' + slot + (salvaged ? ' → 가방 가득, 정리' : by === 1 ? ' · 펫' : ''), color: gradeColor(g) };
}

/**
 * 줍기 띠 배치(c015, UI는 플레이를 가리지 않는다): 화면 왼쪽 가장자리 안전 영역 안, 줄 최대 3개, 너비 min(240px, 화면 28%).
 * 이전 240px 고정 폭은 좁은 화면(360px)에서 가운데 영웅(50%)에 닿았다. 띠는 HUD 배율(scale)로 커지므로 폭을 배율로 나눠 화면상 폭을 지킨다
 */
export const LOOT_FEED_LAYOUT = { rows: 3, maxPx: 240, maxVw: 28, minScale: 0.8 } as const;
/** 띠 상자 max-width CSS(배율 적용 전 값). 화면상 폭 = min(240px, 28vw) */
export function lootFeedMaxWidthCss(scale: number): string {
  const k = Math.max(LOOT_FEED_LAYOUT.minScale, Number.isFinite(scale) && scale > 0 ? scale : 1);
  return `calc(min(${LOOT_FEED_LAYOUT.maxPx}px, ${LOOT_FEED_LAYOUT.maxVw}vw) / ${k.toFixed(3)})`;
}
/**
 * 줍기 띠 상자 화면상 폭(px): min(240, 게임 화면 너비의 28%)이되 그림·이름·종류 태그가 들어갈 최소 폭(150px) 아래로는 줄이지 않는다.
 * viewW = shared/viewport의 게임 화면 너비 — CSS vw는 돌린(세로로 든) 화면에서 실제 화면 너비라 28vw가 109px로 줄어들었다(PR #229 리뷰 P1)
 */
export const LOOT_FEED_MIN_PX = 150;
export function lootFeedBoxWidthPx(viewW: number): number {
  const w = Number.isFinite(viewW) && viewW > 0 ? viewW : 0;
  return Math.max(LOOT_FEED_MIN_PX, Math.min(LOOT_FEED_LAYOUT.maxPx, (w * LOOT_FEED_LAYOUT.maxVw) / 100));
}
/** 화면상 띠 오른쪽 끝(px): 왼쪽 안전 영역 + min(240px, 28vw). 영웅(화면 가운데)과 겹치는지 재는 시험용 */
export function lootFeedRightEdgePx(viewportW: number, safeLeft = 0): number {
  return safeLeft + Math.min(LOOT_FEED_LAYOUT.maxPx, (viewportW * LOOT_FEED_LAYOUT.maxVw) / 100);
}

/**
 * 정리 두 단계 확인(c015, PR #154 리뷰 이월 7번): 세트·전설은 한 번 누르면 확인 줄을 띄우고, 확인 버튼을 눌러야 정리 명령을 보낸다.
 * confirm()은 일부 웹뷰에서 막혀 있어 화면 안에서 묻는다(손상 복구와 같은 방식). 일반·희귀·영웅은 한 번에 정리한다.
 */
export function salvageNeedsConfirm(grade: Grade): boolean { return grade === 'set' || grade === 'legend'; }
export class SalvageConfirm {
  /** 확인을 기다리는 장비 uid(없으면 null) */
  armed: string | null = null;
  /** 정리 버튼: 'send'면 바로 명령을 보내고, 'ask'면 확인 줄을 띄운다 */
  press(uid: string, grade: Grade): 'send' | 'ask' {
    if (!salvageNeedsConfirm(grade)) { this.armed = null; return 'send'; }
    this.armed = uid;
    return 'ask';
  }
  /** 확인 버튼: 확인을 기다리던 그 장비면 true(명령을 보낸다) */
  confirm(uid: string): boolean {
    const ok = this.armed === uid;
    this.armed = null;
    return ok;
  }
  cancel(): void { this.armed = null; }
  /** 선택이 바뀌거나 창이 열리면 확인을 푼다(다른 장비를 실수로 정리하지 않게) */
  sync(selected: string | null): void { if (this.armed !== null && this.armed !== selected) this.armed = null; }
}
